<template>
  <div
    class="upload-input-wrap"
    :class="{
      succ: status === 'succ',
    }"
    :style="{
      width: `${width}px`,
      height: `${height}px`,
    }"
  >
    <!-- 上传成功提示 -->
    <div
      v-if="status === 'succ'"
      class="upload-video-succ"
      :class="disable && 'disable'"
    >
      <div :style="`background-image:url(${preview});`" class="pic" />
      <span
        v-if="!originateClient"
        class="btn-remove"
        @click="handleRemoveVideoResource"
      >
        <i class="el-icon-delete" />
      </span>
      <i class="icon-upload-succ" />
      <p class="upload-succ-text">
        上传成功
      </p>
    </div>
    <!-- 上传过程中提示 -->
    <div v-else-if="status === 'process'" class="upload-video-process">
      <Loading :rotate="uploadComplete" class="loading-progress" />
      <template v-if="uploadComplete < 100">
        <p class="upload-process-text">
          上传中({{ uploadComplete }}%)
        </p>
        <p v-if="uploadSpeed" class="upload-speed">
          上传速率 {{ uploadSpeed }}
        </p>
      </template>
      <p v-else class="upload-process-text">
        正在处理中...
      </p>
    </div>
    <!-- 上传组件 -->
    <el-upload
      v-if="!disable"
      v-show="status === 'wait'"
      :http-request="handleVideoUpload"
      accept=".mp4"
      action
      class="ugc-upload-input"
      drag
    >
      <div ref="uploadInput">
        <template v-if="platform === 'pc'">
          <div class="ctn">
            <span class="icon-pc" />
            <div class="tips">
              将电脑动态壁纸拖拽到这里<br>文件不超过{{ size / 1024 / 1024 }}M
            </div>
          </div>
        </template>
        <template v-else>
          <div class="ctn small-mobile">
            <span class="icon-mobile" />
            <div class="tips">
              将手机动态壁纸<br>拖拽到这里<br>文件不超过{{
                size / 1024 / 1024
              }}M
            </div>
          </div>
        </template>
      </div>
    </el-upload>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as SparkMD5 from 'spark-md5'

import {
  doCheckIsExists,
  doCheckUploadLimit,
  uploadMultipartComplete,
  uploadMultipartInit,
  uploadMultipartUpload,
} from '@/api/upload'

import { parseSpeed } from '@/utils/shared'

import Loading from '@/components/Loading'

export default {
  name: 'UploadVideo',
  components: {
    Loading,
  },
  props: {
    disable: {
      type: Boolean,
      default: false,
    },
    preResourceId: {
      type: Number,
      default: 0,
    },
    preResourceUrl: {
      type: String,
      default: '',
    },
    prePreviewUrl: {
      type: String,
      default: '',
    },
    platform: {
      type: String,
      default: 'pc',
    },
    width: {
      type: Number,
      default: 500,
    },
    height: {
      type: Number,
      default: 280,
    },
    videoWidth: {
      type: Number,
      default: 1920,
    },
    mobileVideoWidth: {
      type: Number,
      default: 720,
    },
    mobileVideoHeight: {
      type: Number,
      default: 1280,
    },
    videoHeight: {
      type: Number,
      default: 1080,
    },
    size: {
      type: Number,
      default: 100 * 1024 * 1024,
    },
    originateClient: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let status = 'wait'
    if (this.preResourceId > 0) {
      status = 'succ'
    }

    return {
      chunkSize: 2097152, // 2M 作为一个chunk
      fileMaxSize: this.size, // 100M
      status, // wait: 等待上传 process: 上传中 succ: 上传成功
      uploadComplete: 50,
      uploadSpeed: '',
      uploadPreLoaded: 0,
      uploadLoaded: 0,
      speedTimer: null,
      resourceId: this.preResourceId,
      resourceUrl: this.preResourceUrl,
      preview: this.prePreviewUrl,
      confirmDialogText: '',
    }
  },
  computed: {
    ...mapState({
      common: (state) => {
        return {
          token: state.userInfo.token,
          open_id: state.userInfo.open_id,
        }
      },
    }),
  },
  watch: {
    confirmDialogText(text) {
      if (text) {
        this.$emit('error', text)
      }
      this.confirmDialogText = ''
    },
    preResourceId(id) {
      if (id) {
        this.resourceId = id
        this.status = 'succ'
      }
    },
    preResourceUrl(url) {
      if (url) {
        this.resourceUrl = url
      }
    },
    prePreviewUrl(url) {
      if (url) {
        this.preview = url
      }
    },
  },
  methods: {
    async checkMd5(md5) {
      try {
        const res = await this.doCheckIsExists(md5)
        const is_exist
          = res.is_exist !== undefined ? res.is_exist : res.data.is_exist
        if (is_exist) {
          if (res.data && res.data.resource_id) {
            this.resourceId = res.data.resource_id
            this.resourceUrl = res.data.resource_url
            this.preview = res.data.preview_jpg
            this.$emit('change', {
              resourceId: this.resourceId,
              resourceUrl: this.resourceUrl,
              preview: this.preview,
            })
            this.status = 'succ'
          } else {
            this.confirmDialogText = '已经上传过该视频，无需重复上传'
          }
          return false
        }
        return true
      } catch (err) {
        // TODO
      }
    },
    checkVideoWidthHeight(file) {
      const $video = document.createElement('video')
      try {
        return new Promise((resolve /* , reject */) => {
          $video.onloadedmetadata = () => {
            const { videoWidth, videoHeight } = $video
            if (videoWidth === 0 || videoHeight === 0) {
              resolve(true)
              return
            }

            if (
              this.platform === 'mobile'
              && videoWidth >= this.mobileVideoWidth
              && videoHeight >= this.mobileVideoHeight
            ) {
              resolve(true)
              return
            }

            if (
              videoWidth >= this.videoWidth
              && videoHeight >= this.videoHeight
            ) {
              resolve(true)
              return
            }

            resolve(false)
          }

          $video.onerror = () => {
            // this.confirmDialogText = `请上传编码格式为H264的MP4文件`
            // reject(false)
            resolve(true)
          }

          try {
            $video.src = URL.createObjectURL(file)
          } catch (err) {
            resolve(true)
          }
        })
      } catch (err) {
        return true
      }
    },
    handleSelectUpload() {
      this.$refs.uploadInput.click()
    },

    getErrorMsg(err) {
      if (!err) return ''
      if (err.msg) return err.msg
      if (err.resp_common && err.resp_common.msg) return err.resp_common.msg
    },

    async handleVideoUpload($event) {
      try {
        await doCheckUploadLimit(
          {
            common: this.common,
          },
          this.platform,
        )
      } catch (err) {
        this.confirmDialogText
          = this.getErrorMsg(err) || '您今日上传壁纸已超过限制，请明天再来哦~'
        return
      }

      const blobSlice = File.prototype.slice
      const file = $event.file

      if (!file.name || !file.name.match(/\.mp4$/gi)) {
        this.confirmDialogText = '请上传MP4格式文件'
        return
      }

      // 检查视频分辨率
      if ((await this.checkVideoWidthHeight(file)) === false) {
        if (this.platform === 'mobile') {
          this.confirmDialogText = `请上传大小不小于${this.mobileVideoWidth}*${this.mobileVideoHeight}的视频文件`
          return
        }
        this.confirmDialogText = `请上传大小不小于${this.videoWidth}*${this.videoHeight}的视频文件`
        return
      }

      if (file.size > this.fileMaxSize) {
        this.confirmDialogText = `上传的视频不能超过 ${Math.ceil(
          this.fileMaxSize / 1024 / 1024,
        )}M`
        return
      }
      const chunkSize = this.chunkSize // chunk 大小
      const chunks = Math.ceil(file.size / chunkSize) // chunk 数量
      let currentChunk = 0 //
      const spark = new SparkMD5.ArrayBuffer()
      const fileReader = new FileReader()
      const fileData = []

      fileReader.onload = async (e) => {
        // console.log('read chunk nr', currentChunk + 1, 'of', chunks)
        spark.append(e.target.result)
        fileData.push(e.target.result)
        currentChunk++
        if (currentChunk < chunks) {
          loadNext()
        } else {
          // console.log('finished loading', spark.end())
          const md5 = spark.end()
          if (await this.checkMd5(md5)) {
            this.multipartUpload(md5, file, fileData)
          }
        }
      }

      function loadNext() {
        const start = currentChunk * chunkSize
        const end
          = start + chunkSize >= file.size ? file.size : start + chunkSize
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      loadNext()
    },

    async multipartUpload(md5, file, fileData) {
      const params = {
        common: this.common,
        file_name: file.name,
        md5,
        file_type: 'live',
      }
      this.status = 'process'
      try {
        // 计算进度
        this.uploadComplete = 0
        // 计算速率
        this.uploadSpeed = ''
        this.uploadPreLoaded = 0
        this.uploadLoaded = 0
        // let time = 0
        this.speedTimer = setInterval(() => {
          // time++
          const speed = (this.uploadLoaded - this.uploadPreLoaded) / 1024
          if (speed) {
            this.uploadSpeed = parseSpeed(speed * 200)
          }
          // console.log('time:', time, 'speed:', speed, this.uploadSpeed, this.uploadPreLoaded)
          this.uploadPreLoaded = this.uploadLoaded
        }, 2000)
        const initRes = await uploadMultipartInit(params, this.platform)

        if (!initRes.resp_common || initRes.resp_common.ret) {
          return
        }
        let completeLoaded = 0

        const uploadId = initRes.data.upload_id
        for (let i = 0, len = fileData.length; i < len; i++) {
          const item = fileData[i]

          // let nextSize = Math.min((i + 1) * this.chunkSize, file.size)

          const formData = new FormData()
          const blobData = new Blob([item])
          formData.append('file_part', blobData)
          formData.append('open_id', this.common.open_id)
          formData.append('token', this.common.token)
          formData.append('upload_id', uploadId)
          formData.append('part_size', blobData.size)
          formData.append('part_number', i + 1)

          const uploadRes = await uploadMultipartUpload(
            formData,
            {
              onUploadProgress: (progressEvent) => {
                const complete
                  = ((progressEvent.loaded / progressEvent.total) * 100) | 0

                this.uploadLoaded = completeLoaded + progressEvent.loaded
                this.uploadComplete
                  = ((this.uploadLoaded / file.size) * 100) | 0
                if (complete === 100) {
                  completeLoaded += progressEvent.loaded
                }
              },
            },
            this.platform,
          )

          if (!uploadRes.resp_common || initRes.resp_common.ret) {
            return
          }
        }

        this.uploadComplete = 100
        clearInterval(this.speedTimer)

        params.upload_id = uploadId
        const completeRes = await uploadMultipartComplete(
          params,
          undefined,
          this.platform,
        )

        if (!completeRes.resp_common || completeRes.resp_common.ret) {
          return
        }

        // console.log('complete res:', completeRes)
        this.resourceId = completeRes.data.resource_id
        this.resourceUrl = completeRes.data.resource_url
        this.preview = completeRes.data.preview_jpg
        this.$emit('change', {
          resourceId: this.resourceId,
          resourceUrl: this.resourceUrl,
          preview: this.preview,
        })
        this.status = 'succ'
      } catch (err) {
        this.status = 'wait'

        if (err.resp_common) {
          if (err.resp_common.ret === 4001002) {
            return
          }

          this.confirmDialogText = err.resp_common.msg
          return
        }

        this.confirmDialogText = err.message
      }

      clearInterval(this.speedTimer)
    },
    handleRemoveVideoResource() {
      this.resourceId = 0
      this.resourceUrl = ''
      this.$emit('change', {
        resourceId: 0,
        resourceUrl: '',
        preview: '',
      })
      this.status = 'wait'
    },
    async doCheckIsExists(md5) {
      try {
        return await doCheckIsExists(
          {
            common: this.common,
            md5,
          },
          this.platform,
        )
      } catch (err) {
        // TODO
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-input-wrap {
  z-index: 1;
  position: relative;
  outline: 2px dashed #ccc;
  border-radius: 2px;
  &.succ {
    outline: none;
  }
  .ctn {
    position: absolute;
    left: 50%;
    top: 50%;
    // width: 155px;
    // height: 135px;
    margin: -77px 0 0 -67px;
    &.small-mobile {
      margin: -75px 0 0 -42px;
    }
  }
  .tips {
    margin-top: 16px;
    text-align: center;
    font-size: 12px;
    color: #a5a5ae;
  }
  .icon-pc {
    display: block;
    width: 130px;
    height: 102px;
    margin: 0 auto;
    background: url("~@/assets/images/upload/icon-pc.png") 0 0 no-repeat;
  }
  .icon-mobile {
    display: block;
    width: 61px;
    height: 102px;
    margin: 0 auto;
    background: url("~@/assets/images/upload/icon-mobile.png") 0 0 no-repeat;
  }
}

.upload-video-succ {
  position: relative;
  width: 100%;
  height: 100%;
  &::after {
    content: "";
    z-index: 1;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(245, 185, 3, 0.3);
  }
  .pic {
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    opacity: 0.2;
  }
  .btn-remove {
    z-index: 2;
    position: absolute;
    right: 10px;
    top: 10px;
    width: 24px;
    height: 24px;
    line-height: 24px;
    text-align: center;
    font-size: 14px;
    background: #333;
    border-radius: 4px;
    color: #999;
    cursor: pointer;
  }
  .icon-upload-succ {
    z-index: 2;
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100px;
    height: 66px;
    margin: -57px 0 0 -50px;
    background: url("~@/assets/images/icon-upload-succ.png") 0 0 no-repeat;
    background-size: 100%;
  }
  .upload-succ-text {
    z-index: 2;
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    text-align: center;
    // font-weight: 700;
    margin-top: 15px;
    font-size: 14px;
    color: #333;
  }
}

.loading-progress {
  position: absolute;
  left: 50%;
  top: 50%;
  margin: -53px 0 0 -53px;
}

.upload-video-process {
  position: relative;
  width: 100%;
  height: 100%;
  .upload-process-text {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    margin-top: 65px;
    text-align: center;
    font-size: 16px;
    color: #333;
  }
  .upload-speed {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    margin-top: 95px;
    text-align: center;
    font-size: 12px;
    color: #a5a5a5;
  }
}
</style>

<style lang="scss">
.ugc-upload-input {
  width: 100%;
  height: 100%;
  .el-upload {
    width: 100%;
    height: 100%;
  }
  .el-upload-dragger {
    width: 100%;
    height: 100%;
    border: none;
  }
  .el-upload-list {
    display: none;
  }
}
.disable {
  cursor: not-allowed;
}
</style>
